import { useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { COMPANY, CITIES } from "@/lib/constants";
import { api, formatApiError } from "@/lib/api";
import Reveal, { SectionHead } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const Kontakt = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success("Dziękujemy! Oddzwonimy lub odpiszemy najszybciej, jak to możliwe.");
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO
        title="Kontakt i darmowa wycena | AD Brikman Łęczna 535 616 324"
        description="Skontaktuj się z AD Brikman Usługi Brukarskie: tel. 535 616 324, adbrikman@gmail.com, ul. Wiosenna 7, 21-010 Łęczna. Bezpłatna wycena — Łęczna, Lublin, Świdnik i okolice."
        path="/kontakt"
        jsonLd={breadcrumbLd([{ label: "Strona główna", path: "/" }, { label: "Kontakt", path: "/kontakt" }])}
      />

      <section className="grain relative overflow-hidden bg-ink pt-36 pb-16 lg:pt-44">
        <div className="paver-bg absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-5">Kontakt</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display max-w-4xl text-5xl font-black uppercase leading-[0.95] tracking-tight text-pearl sm:text-7xl" data-testid="kontakt-title">
              Porozmawiajmy o <span className="text-amber">Twoim bruku</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8" data-testid="kontakt-details">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Reveal>
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="kontakt-card-phone" className="card-edge block h-full p-8">
              <p className="eyebrow mb-4">Telefon</p>
              <p className="font-display text-3xl font-extrabold text-pearl">{COMPANY.phone}</p>
              <p className="mt-2 text-sm text-stonemute">Zadzwoń — najszybsza forma kontaktu</p>
            </a>
          </Reveal>
          <Reveal delay={0.06}>
            <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="kontakt-card-whatsapp" className="card-edge block h-full p-8">
              <p className="eyebrow mb-4">WhatsApp</p>
              <p className="font-display flex items-center gap-3 text-3xl font-extrabold text-pearl">
                <WhatsAppIcon className="text-[#25D366]" size={26} />
                Napisz
              </p>
              <p className="mt-2 text-sm text-stonemute">Szybkie pytanie na czacie</p>
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            <a href={`mailto:${COMPANY.email}`} data-testid="kontakt-card-email" className="card-edge block h-full p-8">
              <p className="eyebrow mb-4">E-mail</p>
              <p className="break-all text-xl font-semibold text-pearl">{COMPANY.email}</p>
              <p className="mt-2 text-sm text-stonemute">Opisz inwestycję — odpiszemy z propozycją</p>
            </a>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="card-edge h-full p-8" data-testid="kontakt-card-address">
              <p className="eyebrow mb-4">Adres</p>
              <p className="text-xl font-semibold text-pearl">{COMPANY.street}</p>
              <p className="text-xl font-semibold text-pearl">{COMPANY.postal}</p>
              <p className="mt-2 text-sm text-stonemute">{COMPANY.region}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-4 pb-24 sm:px-8 lg:grid-cols-2" data-testid="kontakt-form-section">
        <div>
          <SectionHead eyebrow="Bezpłatna wycena" title="Formularz" sub="Napisz, co planujesz — podjazd, taras, opaskę. Oddzwonimy i umówimy wizję lokalną." />
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <input required placeholder="Imię" value={form.name} onChange={set("name")} data-testid="contact-name" className="field w-full px-5 py-3.5" />
            <input required placeholder="Telefon" type="tel" value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="field w-full px-5 py-3.5" />
            <input placeholder="E-mail (opcjonalnie)" type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="field w-full px-5 py-3.5" />
            <input placeholder="Miejscowość" value={form.city} onChange={set("city")} data-testid="contact-city" className="field w-full px-5 py-3.5" />
            <textarea required placeholder="Opis inwestycji (np. podjazd 60 m², kostka grafit)" rows="5" value={form.message} onChange={set("message")} data-testid="contact-message" className="field w-full px-5 py-3.5" />
            <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-amber w-full px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] disabled:opacity-50">
              {sending ? "Wysyłanie…" : "Wyślij zapytanie"}
            </button>
          </form>
        </div>

        <div>
          <SectionHead eyebrow="Obszar działania" title="Gdzie dojeżdżamy" />
          <div className="flex flex-wrap gap-3">
            {CITIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.03}>
                <span className="card-edge notch-sm inline-block px-5 py-2.5 font-display text-lg font-bold uppercase text-pearl" data-testid={`kontakt-city-${c.toLowerCase()}`}>
                  {c}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md leading-relaxed text-stonemute">
              Działamy na terenie województwa lubelskiego. Jeśli Twoja miejscowość nie jest na liście — zadzwoń, na pewno się dogadamy.
            </p>
          </Reveal>
        </div>
      </section>

      <Toaster position="bottom-center" theme="dark" />
    </>
  );
};

export default Kontakt;
